<template>
  <header class="lead-flow-modal-header">
    <div class="lead-flow-modal-header__identity">
      <span
        v-if="countryCode"
        :class="`fflag ff-md fflag-${countryCode}`"
        v-tooltip="{ content: countryName, placement: 'bottom', offset: 8 }"
        class="lead-flow-modal-header__flag"
        :aria-label="countryName"
      ></span>
      <img
        v-else
        v-tooltip="{ content: 'Country not provided', placement: 'bottom', offset: 8 }"
        class="lead-flow-modal-header__globe"
        src="@/assets/img/globe-icon.svg"
        alt="Country not provided"
      >

      <span
        v-if="loading"
        class="lead-flow-modal-header__skeleton lead-flow-modal-header__skeleton--title"
        aria-hidden="true"
      ></span>
      <h2
        v-else
        v-tooltip="{ content: leadName, placement: 'bottom', offset: 8 }"
        class="lead-flow-modal-header__title"
      >
        {{ leadName }}
      </h2>

      <span
        v-if="loading"
        class="lead-flow-modal-header__skeleton lead-flow-modal-header__skeleton--stage"
        aria-hidden="true"
      ></span>
      <AppStatusBadge
        v-else-if="stageName"
        v-tooltip="{ content: stageName, placement: 'bottom', offset: 8 }"
        design="new"
        class="lead-flow-modal-header__stage"
        :color="stageColor"
        :label="stageName"
      />
    </div>

    <div class="lead-flow-modal-header__actions">
      <AskAIButton @click="$emit('ask-ai')" />
      <div class="lead-flow-modal-header__manager">
        <span
          v-if="loading"
          class="lead-flow-modal-header__skeleton lead-flow-modal-header__skeleton--avatar"
          aria-hidden="true"
        ></span>
        <template v-else>
          <img
            v-if="managerAvatar"
            class="lead-flow-modal-header__avatar"
            :src="managerAvatar"
            alt=""
          >
          <span v-else class="lead-flow-modal-header__initials" aria-hidden="true">
            {{ managerInitials }}
          </span>
        </template>
        <span
          v-if="loading"
          class="lead-flow-modal-header__skeleton lead-flow-modal-header__skeleton--manager"
          aria-hidden="true"
        ></span>
        <strong v-else v-tooltip="{ content: managerName, placement: 'bottom', offset: 8 }">
          {{ managerName }}
        </strong>
      </div>

      <a
        class="lead-flow-modal-header__open"
        :href="leadUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        Open Lead
        <img src="@/assets/img/table-link-icon.svg" alt="">
      </a>

      <AppButton
        v-if="leadFlowDebug"
        design="new"
        theme="dark"
        class="lead-flow-modal-header__trengo-sync"
        type="button"
        :title="trengoSyncPending ? 'Importing Trengo messages' : 'Import latest Trengo messages'"
        :aria-label="trengoSyncPending ? 'Importing Trengo messages' : 'Import latest Trengo messages'"
        :disabled="trengoSyncPending"
        @click.native="$emit('sync-trengo')"
      >
        <img src="@/assets/img/download-table-blue-icon.svg" alt="">
      </AppButton>

      <AppButton
        v-if="leadFlowDebug"
        design="new"
        theme="dark"
        class="lead-flow-modal-header__refresh"
        type="button"
        title="Refresh lead workspace"
        aria-label="Refresh lead workspace"
        @click.native="$emit('refresh')"
      >
        <img src="@/assets/img/update-small-icon.svg" alt="">
      </AppButton>

      <AppModalClose
        design="new"
        class="lead-flow-modal-header__close"
        @click="$emit('close')"
      />
    </div>
  </header>
</template>

<script>
import AppModalClose from '@/components/AppModalClose'
import AskAIButton from '@/components/knowledge-ai/AskAIButton.vue'

export default {
  name: 'LeadFlowModalHeader',
  components: { AppModalClose, AskAIButton },
  props: {
    detail: {
      type: Object,
      default: null
    },
    leadId: {
      type: Number,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    trengoSyncPending: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    leadFlowDebug() {
      return process.env.VUE_APP_LEAD_FLOW_DEBUG === 'true'
    },
    countryCode() {
      return this.detail && this.detail.country && this.detail.country.code ? this.detail.country.code : ''
    },
    countryName() {
      return this.detail && this.detail.country && this.detail.country.name ? this.detail.country.name : 'Country not provided'
    },
    leadName() {
      return this.detail && this.detail.name ? this.detail.name : `Lead #${this.leadId}`
    },
    stageName() {
      return this.detail && this.detail.stage && this.detail.stage.name ? this.detail.stage.name : ''
    },
    stageColor() {
      return this.detail && this.detail.stage && this.detail.stage.color ? this.detail.stage.color : '#607174'
    },
    manager() {
      return this.detail && this.detail.manager ? this.detail.manager : null
    },
    managerName() {
      if (!this.manager) return 'Unassigned'
      return this.manager.full_name || this.manager.name || 'Unassigned'
    },
    managerInitials() {
      if (!this.manager) return '—'
      return this.managerName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('') || '—'
    },
    managerAvatar() {
      if (!this.manager) return ''
      return this.manager.avatar_url
        || this.manager.avatar
        || this.manager.photo_url
        || this.manager.photo
        || (this.manager.crop && this.manager.crop.crop_url)
        || ''
    },
    leadUrl() {
      return `/crm/leads/${this.leadId}`
    }
  }
}
</script>

<style scoped>
.lead-flow-modal-header {
  position: sticky;
  z-index: 4;
  top: 0;
  display: flex;
  min-height: 76px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 17px 24px;
  border-bottom: 1px solid #e2e5df;
  background: #fff;
  box-shadow: 0 2px 6px rgba(29, 45, 42, .04);
}

.lead-flow-modal-header__identity,
.lead-flow-modal-header__actions,
.lead-flow-modal-header__manager {
  display: flex;
  min-width: 0;
  align-items: center;
}

.lead-flow-modal-header__identity {
  flex: 1 1 auto;
  gap: 11px;
}

.lead-flow-modal-header__actions {
  flex: 0 0 auto;
  gap: 10px;
}

.lead-flow-modal-header__flag {
  width: 27px;
  height: 17px;
  flex: 0 0 auto;
}

.lead-flow-modal-header__globe {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  filter: grayscale(1);
  opacity: .55;
}

.lead-flow-modal-header__title {
  min-width: 0;
  overflow: hidden;
  margin: 0;
  color: #17292c;
  font-size: 22px;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-flow-modal-header__stage {
  display: inline-flex;
  max-width: 150px;
  min-height: 25px;
  flex: 0 0 auto;
  align-items: center;
  overflow: hidden;
  padding: 4px 10px;
  border-radius: 13px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-flow-modal-header__manager {
  max-width: 240px;
  gap: 8px;
  color: #526365;
  font-size: 14px;
}

.lead-flow-modal-header__manager strong {
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-flow-modal-header__avatar,
.lead-flow-modal-header__initials {
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.lead-flow-modal-header__avatar {
  object-fit: cover;
}

.lead-flow-modal-header__initials {
  background: #e8eee4;
  color: #477615;
  font-size: 10px;
  font-weight: 700;
}

.lead-flow-modal-header__open {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  gap: 7px;
  padding: 7px 11px;
  border: 1px solid #d8ded6;
  border-radius: 5px;
  background: #fff;
  color: #35494b;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.lead-flow-modal-header__open:hover {
  border-color: #9fbd75;
  color: #4d7f17;
}

.lead-flow-modal-header__open img {
  width: 14px;
  height: 14px;
  filter: invert(1);
  opacity: .65;
}

.lead-flow-modal-header__trengo-sync,
.lead-flow-modal-header__refresh {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #e2e5df;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
}

.lead-flow-modal-header__trengo-sync:hover,
.lead-flow-modal-header__trengo-sync:focus,
.lead-flow-modal-header__refresh:hover,
.lead-flow-modal-header__refresh:focus {
  background: #f4f5f1;
}

.lead-flow-modal-header__trengo-sync:disabled {
  cursor: wait;
  opacity: .55;
}

.lead-flow-modal-header__trengo-sync img,
.lead-flow-modal-header__refresh img {
  width: 25px;
  height: 25px;
}

.lead-flow-modal-header__skeleton {
  display: block;
  border-radius: 5px;
  background: linear-gradient(90deg, #e7ebe6 25%, #f6f7f4 50%, #e7ebe6 75%);
  background-size: 200% 100%;
  animation: lead-flow-modal-header-pulse 1.4s ease-in-out infinite;
}

.lead-flow-modal-header__skeleton--title {
  width: 210px;
  height: 25px;
}

.lead-flow-modal-header__skeleton--stage {
  width: 72px;
  height: 25px;
  border-radius: 13px;
}

.lead-flow-modal-header__skeleton--avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

.lead-flow-modal-header__skeleton--manager {
  width: 90px;
  height: 13px;
}

@keyframes lead-flow-modal-header-pulse {
  from {
    background-position: 100% 0;
  }

  to {
    background-position: -100% 0;
  }
}

@media (max-width: 800px) {
  .lead-flow-modal-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .lead-flow-modal-header__identity,
  .lead-flow-modal-header__actions {
    width: 100%;
  }

  .lead-flow-modal-header__actions {
    justify-content: flex-end;
  }
}

</style>
